import {
  Avatar,
  Badge,
  Divider,
  Group,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { ConversationRow } from './ConversationRow';
import type { ChannelCapabilities, InboxAdapter, InboxListItem } from './types';

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most recent' },
  { value: 'unread', label: 'Unread first' },
  { value: 'alpha', label: 'Name (A–Z)' },
];

/** The conversation sidebar — title, optional Page switcher / search / sort,
 *  and rows. */
export function ConversationList({
  items,
  isPending,
  activeId,
  onSelect,
  capabilities,
  search,
  onSearch,
  sortBy,
  onSort,
  listScope,
}: {
  items: InboxListItem[];
  isPending: boolean;
  activeId: number | null;
  onSelect: (id: number) => void;
  capabilities: ChannelCapabilities;
  search: string;
  onSearch: (v: string) => void;
  sortBy: 'recent' | 'unread' | 'alpha';
  onSort: (v: 'recent' | 'unread' | 'alpha') => void;
  listScope?: InboxAdapter['listScope'];
}) {
  const scopeOptions = listScope?.options ?? [];
  const scopeByValue = new Map(scopeOptions.map((o) => [o.value, o]));
  const activeScope = listScope ? scopeByValue.get(listScope.value) : undefined;
  return (
    <>
      <Stack p="sm" gap="xs">
        <Title order={4}>{capabilities.listTitle}</Title>
        {/* Page / account switcher — only when ≥2 connected; a single
            connection shows its name as a static caption instead. */}
        {listScope && scopeOptions.length >= 2 && (
          <Select
            size="xs"
            aria-label="Switch Page"
            data={scopeOptions.map((o) => ({ value: o.value, label: o.label }))}
            value={listScope.value}
            onChange={(v) => {
              if (v) listScope.onChange(v);
            }}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            leftSection={
              <Avatar size={18} radius="xl" src={activeScope?.avatarUrl ?? undefined}>
                {(activeScope?.label?.[0] ?? '?').toUpperCase()}
              </Avatar>
            }
            renderOption={({ option }) => {
              const o = scopeByValue.get(option.value);
              return (
                <Group
                  gap="xs"
                  wrap="nowrap"
                  justify="space-between"
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Avatar size={20} radius="xl" src={o?.avatarUrl ?? undefined}>
                      {(option.label[0] ?? '?').toUpperCase()}
                    </Avatar>
                    <Text size="sm" truncate>
                      {option.label}
                    </Text>
                  </Group>
                  {o?.unreadCount ? (
                    <Badge size="xs" circle color={capabilities.accent}>
                      {o.unreadCount}
                    </Badge>
                  ) : null}
                </Group>
              );
            }}
          />
        )}
        {listScope && scopeOptions.length === 1 && (
          <Group gap="xs" wrap="nowrap">
            <Avatar size={18} radius="xl" src={scopeOptions[0]!.avatarUrl ?? undefined}>
              {(scopeOptions[0]!.label[0] ?? '?').toUpperCase()}
            </Avatar>
            <Text size="xs" c="dimmed" fw={500} truncate>
              {scopeOptions[0]!.label}
            </Text>
          </Group>
        )}
        {capabilities.list.showSearch && (
          <TextInput
            size="xs"
            placeholder="Search name or ID"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => onSearch(e.currentTarget.value)}
          />
        )}
        {capabilities.list.showSort && (
          <Select
            size="xs"
            data={SORT_OPTIONS}
            value={sortBy}
            onChange={(v) => onSort((v as 'recent' | 'unread' | 'alpha') ?? 'recent')}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        )}
      </Stack>
      <Divider />
      <ScrollArea style={{ flex: 1 }}>
        {isPending ? (
          <Stack p="sm">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} height={56} radius="sm" />
            ))}
          </Stack>
        ) : (
          <Stack gap={0}>
            {items.map((c) => (
              <ConversationRow
                key={c.id}
                item={c}
                active={c.id === activeId}
                capabilities={capabilities}
                onSelect={() => onSelect(c.id)}
              />
            ))}
            {items.length === 0 && (
              <Text c="dimmed" ta="center" p="md">
                {search.trim() ? 'No matches.' : capabilities.emptyListLabel}
              </Text>
            )}
          </Stack>
        )}
      </ScrollArea>
    </>
  );
}
